<template>
  <nav class="navbar" role="navigation" aria-label="main navigation">
    <!-- LOGO -->
    <div class="d-flex flex-row fl-j-c">
      <div class="navbar-brand">
        <router-link
          :to="{ path: '/' }"
          class="navbar--logo navbar-item"
          tag="div"
        >
          <img :src="smeetzLogo" alt="Smeetz" width="25" />
        </router-link>
      </div>
    </div>

    <!-- RIGHT SIDE -->
    <div class="navbar-end">
      <Suspense v-if="isAuth && !isClosingCashfloat"><NavbarTabs /></Suspense>
      <template v-if="isAuth && hasSalesStarted && isCashbookV2">
        <SSeparator orientation="vertical" style="margin-left: 20px" />
        <div class="navbar-item cash-transfer-wrapper group">
          <div
            class="cash-transfer-button"
            :class="{ 'is-disabled': !isOnline }"
            data-cy="navbar-cash-transfer"
            :title="isOnline ? t('button.transfer-cash') : ''"
            @click="onClickCashTransfer"
          >
            <ArrowUpDownIcon class="w-16 h-7 text-white text-xl mr-1" />
          </div>
          <div
            v-if="!isOnline"
            class="cash-transfer-offline-tooltip"
            data-cy="navbar-cash-transfer-offline-tooltip"
          >
            {{ t("cashtransfer.v2.offline-tooltip") }}
          </div>
        </div>
      </template>
      <SSeparator orientation="vertical" class="my-1.5 mr-2" />
      <div class="navbar-item">
        <intercom />
      </div>
      <SSeparator orientation="vertical" class="my-1.5 mx-2" />
      <div class="navbar-item">
        <!-- RELOAD CURRENT CONFIG -->
        <navbar-reload-config />
      </div>
      <template v-if="isAuth && hasSalesStarted">
        <SSeparator orientation="vertical" class="my-1.5 mx-2" />
        <div class="navbar-item">
          <!-- SCAN QR CODE -->
          <navbar-scan-qr-code />
        </div>
      </template>

      <SSeparator orientation="vertical" class="my-1.5 mx-2" />
      <div class="navbar-item">
        <!-- DETECT INTERNET CONNECTION -->
        <navbar-detect-internet />
      </div>

      <!-- LOCK POS (back to PIN screen) -->
      <template v-if="isAuth && isPinLockAvailable">
        <SSeparator orientation="vertical" class="my-1.5 mx-2" />
        <div class="navbar-item">
          <div
            class="lock-pos-button"
            data-cy="navbar-lock-pos"
            :title="t('pin.lock')"
            @click="lockPos"
          >
            <i class="s-icon s-icon--lock" aria-hidden="true" />
          </div>
        </div>
      </template>

      <SSeparator v-if="isAuth" orientation="vertical" class="my-1.5 mx-2" />
      <!-- MAIN MENU -->
      <template v-if="isAuth">
        <navbar-main-menu class="navbar-item has-cursor" />
      </template>
    </div>

    <!-- loading bar -->
    <!-- <div v-if="isLoading" class="navbar-loading"></div> -->
  </nav>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  ref,
} from "vue";

// modules
import AuthModule from "@/store/modules/auth";

// components
import {
  NavbarMainMenu,
  NavbarDetectInternet,
  NavbarTabs,
  NavbarReloadConfig,
  NavbarScanQrCode,
  Intercom,
} from "@/components/index";
import { useI18n } from "vue-i18n";
import { hasSalesStarted } from "@/utils/vue-helpers/cash-float";
import CashFloatModule from "@/store/modules/cash-float";
import AppModule from "@/store/modules/app";
import { smeetzLogo } from "@smeetz/ui-assets";
import { SSeparator } from "@smeetz/ui-core";
import { ArrowUpDownIcon } from "@smeetz/ui-icons";
import "@smeetz/ui-icons/lock/style.css";
import ModalModule from "@/store/modules/modal";
import { ModalStatus } from "@/constants/modal";
import {
  isCashbookV2FlowEnabled,
  isPinLoginEnabled,
} from "@/utils/helpers/feature-flags";
import { redirectToLoginPage } from "@/utils/helpers/router.helper";

export default defineComponent({
  name: "Navbar",
  components: {
    NavbarDetectInternet,
    Intercom,
    NavbarMainMenu,
    NavbarTabs,
    NavbarReloadConfig,
    NavbarScanQrCode,
    SSeparator,
    ArrowUpDownIcon,
  },
  setup() {
    const { t } = useI18n({});
    const isAuth = computed(() => {
      return AuthModule.getIsAuth;
    });

    const isClosingCashfloat = computed(() => {
      return CashFloatModule.isClosingCashfloat;
    });

    const isLoading = computed(() => {
      return AppModule.isBackgroundLoading;
    });

    const isCashbookV2 = computed(() => isCashbookV2FlowEnabled());

    // Show the lock shortcut wherever PIN login is enabled for this group,
    // regardless of how the current user signed in.
    const isPinLockAvailable = computed(() => isPinLoginEnabled());

    // Lock returns to the PIN screen with no confirmation
    const lockPos = () => {
      AuthModule.UserLogOut();
      redirectToLoginPage();
    };

    // Cash transfers sync to the backend, disable the entry point while
    // offline and surface why on hover.
    const isOnline = ref(
      typeof navigator !== "undefined" ? navigator.onLine : true
    );
    const handleOnline = () => {
      isOnline.value = true;
    };
    const handleOffline = () => {
      isOnline.value = false;
    };
    onMounted(() => {
      window.addEventListener("online", handleOnline);
      window.addEventListener("offline", handleOffline);
    });
    onBeforeUnmount(() => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    });

    function onClickCashTransfer() {
      if (!isOnline.value) return;
      ModalModule.setStatus(ModalStatus.CashInOut);
    }

    return {
      t,
      isAuth,
      hasSalesStarted,
      isClosingCashfloat,
      isLoading,
      smeetzLogo,
      isCashbookV2,
      isPinLockAvailable,
      lockPos,
      isOnline,
      onClickCashTransfer,
    };
  },
});
</script>

<style lang="scss" scoped>
.navbar {
  position: relative;
  display: flex;
  align-items: stretch;

  .navbar-end {
    display: flex;
    align-items: stretch;
    margin-left: auto;
  }

  .order-reference {
    color: white;
    font-weight: 600;
  }
  .navbar-loading {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 6px;
    background: linear-gradient(90deg, var(--primary-smeetz) 0%, #19dbea 100%);
    animation: loadingAnimation 2s linear infinite;
  }
}
.navbar--logo {
  filter: brightness(0) invert(1);
}

.lock-pos-button {
  display: flex;
  align-items: center;
  cursor: pointer;
  color: #fff;
  font-size: 22px;
}

.cash-transfer-wrapper {
  position: relative;

  .cash-transfer-button {
    cursor: pointer;

    &.is-disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  .cash-transfer-offline-tooltip {
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 8px;
    z-index: 30;
    width: 220px;
    padding: 8px 12px;
    border-radius: 6px;
    background-color: #1e293b;
    color: #fff;
    font-size: 12px;
    line-height: 1.3;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.2);
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.15s ease;
  }

  &:hover .cash-transfer-offline-tooltip {
    opacity: 1;
    visibility: visible;
  }
}

@keyframes loadingAnimation {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}
</style>
